import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '../i18n'

const HEADER_HEIGHT = 80

// Flag components as SVG for better quality
const FlagES = () => (
  <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
    <path fill="#c60b1e" d="M0 0h640v480H0z"/>
    <path fill="#ffc400" d="M0 120h640v240H0z"/>
  </svg>
)

const FlagGB = () => (
  <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
    <path fill="#012169" d="M0 0h640v480H0z"/>
    <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
    <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
    <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
  </svg>
)

const FlagRO = () => (
  <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
    <g fillRule="evenodd" strokeWidth="1pt">
      <path fill="#00319c" d="M0 0h213.3v480H0z"/>
      <path fill="#ffde00" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#de2110" d="M426.7 0H640v480H426.7z"/>
    </g>
  </svg>
)

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [isLangOpen, setIsLangOpen] = useState(false)
  const langRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { language, setLanguage, t } = useLanguage()

  const languages = [
    { code: 'es', name: 'Espanol', Flag: FlagES },
    { code: 'en', name: 'English', Flag: FlagGB },
    { code: 'ro', name: 'Romana', Flag: FlagRO }
  ]

  const currentLang = languages.find(l => l.code === language) || languages[1]

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Navigation items with translations
  const navItems = [
    { label: t('nav.services'), type: 'section', id: 'services' },
    { label: t('nav.faq'), type: 'section', id: 'faq' },
    { label: t('nav.coach'), type: 'section', id: 'coach' },
    { label: t('nav.process'), type: 'section', id: 'process' },
    { label: t('nav.gallery'), type: 'section', id: 'gallery' },
    { label: t('nav.camps'), type: 'route', path: '/camps' },
    { label: t('nav.contact'), type: 'route', path: '/contact' }
  ]

  const handleNavClick = (e, item) => {
    e.preventDefault()
    setIsMenuOpen(false)

    if (item.type === 'section') {
      if (location.pathname === '/') {
        const element = document.getElementById(item.id)
        if (element) {
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - HEADER_HEIGHT

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      } else {
        navigate(`/#${item.id}`)
      }
    } else if (item.type === 'route') {
      navigate(item.path)
    }
  }

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode)
    setIsLangOpen(false)
  }

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrollPosition > 50 ? 'bg-gray-900/95 shadow-2xl backdrop-blur-lg' : 'bg-gray-900/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 py-2 md:py-3 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/') }}>
            <img
              src="/logo.png"
              alt="Wind Tunnel Coaching - Professional Indoor Flight Training"
              className="w-[5rem] md:w-[7rem] h-auto transition-all duration-300"
              width="64"
              height="64"
            />
          </a>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.type === 'section' ? `#${item.id}` : item.path}
              onClick={(e) => handleNavClick(e, item)}
              className="text-white hover:text-orange-500 transition-colors text-base font-medium"
            >
              {item.label}
            </a>
          ))}

          {/* Language Selector - Desktop */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all border border-gray-600/50 hover:border-orange-500/50"
              aria-label="Select language"
            >
              <currentLang.Flag />
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence>
              {isLangOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 bg-gray-800 rounded-xl shadow-xl border border-gray-700 overflow-hidden"
                >
                  <div className="flex p-2 gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`p-2 rounded-lg transition-all ${
                          language === lang.code
                            ? 'bg-orange-500/30 ring-2 ring-orange-500'
                            : 'hover:bg-gray-700'
                        }`}
                        title={lang.name}
                      >
                        <lang.Flag />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: Language + Menu Button */}
        <div className="md:hidden flex items-center space-x-3">
          {/* Language Selector - Mobile */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all border border-gray-600/50"
              aria-label="Select language"
            >
              <currentLang.Flag />
            </button>

            <AnimatePresence>
              {isLangOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 bg-gray-800 rounded-xl shadow-xl border border-gray-700 overflow-hidden z-[60]"
                >
                  <div className="flex p-2 gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`p-2 rounded-lg transition-all ${
                          language === lang.code
                            ? 'bg-orange-500/30 ring-2 ring-orange-500'
                            : 'hover:bg-gray-700'
                        }`}
                        title={lang.name}
                      >
                        <lang.Flag />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Menu Button */}
          <button
            className="text-white text-2xl p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden absolute top-full left-0 right-0 bg-gray-900/95 backdrop-blur-lg p-4 space-y-4 z-50"
          >
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.type === 'section' ? `#${item.id}` : item.path}
                onClick={(e) => handleNavClick(e, item)}
                className="block text-white hover:text-orange-500 transition-colors py-2 text-lg"
              >
                {item.label}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Header
